/* DocumentRequestsWorkspace.jsx — iter-P64 · Pilot Ready.
 *
 * Canonical Document Requests workspace mounted below the Current
 * Stage checklist inside the Deal Room.  Zero new SSOT — reads the
 * live `docRequests` array already fetched by the parent room
 * reload, opens the existing RequestDocSheet drawer, and calls the
 * existing CRUD endpoints (`PATCH /api/deals/{id}/document-requests/{req_id}`
 * for status transitions, `DELETE` for cancel).
 *
 * Sort order: Overdue → Due today → Due this week → Outstanding →
 * Completed (collapsed by default).
 *
 * Contextual actions per status:
 *   requested  → Chase · Mark Received · Cancel
 *   uploaded   → View · Verify · Cancel
 *   approved   → View        (row lands in Completed lane)
 *   rejected   → Chase · Cancel
 *   cancelled  → (row hidden from workspace)
 */
import React, { useMemo, useState, useCallback } from "react";
import {
  FileText, Inbox, Plus, ChevronDown, ChevronRight, Send, Check,
  Eye, X as XIcon, ShieldCheck, Clock, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const _fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const _fmtRel = (iso) => {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const _statusTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "approved")  return { bg: "bg-emerald-50", text: "text-emerald-700", label: "Verified" };
  if (s === "uploaded")  return { bg: "bg-violet-50",  text: "text-violet-700",  label: "Received" };
  if (s === "rejected")  return { bg: "bg-rose-50",    text: "text-rose-700",    label: "Rejected" };
  return { bg: "bg-amber-50", text: "text-amber-700", label: "Outstanding" };
};

const _bucket = (r) => {
  const s = String(r.status || "").toLowerCase();
  if (s === "approved") return "completed";
  if (!r.due_at) return "outstanding";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(r.due_at.length === 10 ? r.due_at + "T00:00:00" : r.due_at);
  if (Number.isNaN(due.getTime())) return "outstanding";
  const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0)  return "overdue";
  if (diffDays === 0) return "due_today";
  if (diffDays <= 7)  return "due_this_week";
  return "outstanding";
};

const _bucketMeta = {
  overdue:       { label: "Overdue",      tone: "text-rose-700",    icon: AlertTriangle },
  due_today:     { label: "Due today",    tone: "text-amber-700",   icon: Clock },
  due_this_week: { label: "Due this week", tone: "text-slate-600",  icon: Clock },
  outstanding:   { label: "Outstanding",  tone: "text-slate-500",   icon: Inbox },
  completed:     { label: "Completed",    tone: "text-emerald-700", icon: Check },
};

const _reminderCount = (r) => {
  const rems = Array.isArray(r.reminders) ? r.reminders : [];
  return rems.filter((x) => x?.status === "sent").length;
};

const _buildActivity = (r) => {
  const rows = [];
  if (r.requested_at) rows.push({ kind: "Requested", at: r.requested_at, actor: r.requested_by_name });
  if (r.email_sent_at) rows.push({ kind: "Email sent", at: r.email_sent_at, actor: r.recipient_name || r.recipient_email });
  for (const x of (r.reminders || [])) {
    if (x?.status === "sent" && x?.sent_at) {
      rows.push({ kind: `Reminder · ${x.kind}`, at: x.sent_at, actor: r.recipient_name });
    }
  }
  if (r.uploaded_at) rows.push({ kind: "Uploaded", at: r.uploaded_at, actor: r.uploaded_by_name });
  if (r.approved_at) rows.push({ kind: "Verified", at: r.approved_at, actor: r.approved_by_name });
  if (String(r.status).toLowerCase() === "rejected") rows.push({ kind: "Rejected", at: r.approved_at, actor: r.approved_by_name });
  return rows.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
};

const _actionsForStatus = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "approved")  return ["view"];
  if (s === "uploaded")  return ["view", "verify", "cancel"];
  if (s === "rejected")  return ["chase", "cancel"];
  return ["chase", "received", "cancel"]; // requested / default
};

export function DocumentRequestsWorkspace({
  dealId,
  docRequests = [],
  onRequestDoc,
  onReload,
  onNavigateToChecklistItem,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [completedOpen, setCompletedOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const visible = useMemo(
    () => (docRequests || []).filter((r) => String(r.status || "").toLowerCase() !== "cancelled"),
    [docRequests],
  );

  const buckets = useMemo(() => {
    const b = { overdue: [], due_today: [], due_this_week: [], outstanding: [], completed: [] };
    for (const r of visible) b[_bucket(r)].push(r);
    // Within each bucket, sort by due_at ascending (undated last).
    const _byDue = (a, b_) => {
      const ax = a.due_at ? Date.parse(a.due_at) : Number.POSITIVE_INFINITY;
      const bx = b_.due_at ? Date.parse(b_.due_at) : Number.POSITIVE_INFINITY;
      return ax - bx;
    };
    for (const k of Object.keys(b)) b[k].sort(_byDue);
    return b;
  }, [visible]);

  const summary = useMemo(() => ({
    outstanding: buckets.overdue.length + buckets.due_today.length + buckets.due_this_week.length + buckets.outstanding.length,
    overdue: buckets.overdue.length,
    completed: buckets.completed.length,
  }), [buckets]);

  const applyPatch = useCallback(async (req, body, label) => {
    setPendingAction(req.id);
    try {
      await api.patch(`/api/deals/${dealId}/document-requests/${req.id}`, body);
      toast.success(label);
      onReload?.();
    } catch (e) {
      toast.error("Could not update", { description: e?.response?.data?.detail || e?.message || "Network error" });
    } finally {
      setPendingAction(null);
    }
  }, [dealId, onReload]);

  const onCancel = useCallback(async (req) => {
    if (!window.confirm(`Cancel the request for ${req.label}?`)) return;
    setPendingAction(req.id);
    try {
      await api.delete(`/api/deals/${dealId}/document-requests/${req.id}`);
      toast.success("Request cancelled");
      onReload?.();
    } catch (e) {
      toast.error("Could not cancel", { description: e?.response?.data?.detail || e?.message || "Network error" });
    } finally {
      setPendingAction(null);
    }
  }, [dealId, onReload]);

  const onChase = useCallback(async (req) => {
    // Chase = re-send the request email + increment the sent count.
    // Server-side implementation lives in Phase C; for Pilot we log
    // the chase as a note on the request so the timeline updates.
    const now = new Date().toISOString();
    const nextReminders = [...(req.reminders || []), { kind: "manual", scheduled_for: now, status: "sent", sent_at: now }];
    await applyPatch(req, { note: (req.note ? req.note + "\n" : "") + `Chased ${_fmtRel(now)}` }, "Chase logged");
    // Local optimism — parent reload will re-hydrate from the API.
    void nextReminders;
  }, [applyPatch]);

  const onReceived = useCallback((req) => applyPatch(req, { status: "uploaded" }, "Marked received"), [applyPatch]);
  const onVerify   = useCallback((req) => applyPatch(req, { status: "approved" }, "Verified · progression will update"), [applyPatch]);

  const onView = useCallback((req) => {
    // Deep-link to the linked document when present, else scroll
    // the Documents rail into view.
    if (req.document_id) {
      const el = document.querySelector(`[data-doc-id="${req.document_id}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    }
    const rail = document.querySelector('[data-testid="canonical-rail-documents"]');
    if (rail) rail.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const renderRow = (r) => {
    const tone = _statusTone(r.status);
    const isExpanded = expandedId === r.id;
    const actions = _actionsForStatus(r.status);
    const reminderCount = _reminderCount(r);
    const bucketKey = _bucket(r);
    const bucketMeta = _bucketMeta[bucketKey];
    return (
      <li
        key={r.id}
        className="border border-slate-100 rounded-lg bg-white hover:border-slate-200 transition-colors"
        data-testid={`docreq-row-${r.id}`}
        data-status={String(r.status || "").toLowerCase()}
        data-bucket={bucketKey}
      >
        <button
          type="button"
          onClick={() => setExpandedId(isExpanded ? null : r.id)}
          className="w-full flex items-start gap-3 px-3 py-2 text-left"
          data-testid={`docreq-row-toggle-${r.id}`}
          aria-expanded={isExpanded}
        >
          {isExpanded
            ? <ChevronDown size={13} className="text-slate-400 shrink-0 mt-0.5" />
            : <ChevronRight size={13} className="text-slate-400 shrink-0 mt-0.5" />}
          <FileText size={13} className="text-slate-500 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[12.5px] font-semibold text-slate-900 truncate">{r.label || "Document"}</p>
              <span className={`inline-flex items-center px-1.5 h-[16px] rounded text-[10px] font-medium ${tone.bg} ${tone.text}`} data-testid={`docreq-status-${r.id}`}>
                {tone.label}
              </span>
              {bucketKey === "overdue" && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-rose-700">
                  <AlertTriangle size={9} strokeWidth={2.2} /> Overdue
                </span>
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 flex-wrap text-[10.5px] text-slate-500">
              <span>From <span className="text-slate-700">{r.recipient_name || r.requested_from_label || r.requested_from || "—"}</span></span>
              <span aria-hidden className="text-slate-300">·</span>
              <span data-testid={`docreq-due-${r.id}`}>Due {_fmtDate(r.due_at)}</span>
              {r.doc_type && (
                <>
                  <span aria-hidden className="text-slate-300">·</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onNavigateToChecklistItem?.(r.doc_type); }}
                    className="text-indigo-700 hover:text-indigo-900 font-semibold"
                    data-testid={`docreq-checklist-link-${r.id}`}
                  >
                    Checklist item
                  </button>
                </>
              )}
              {reminderCount > 0 && (
                <>
                  <span aria-hidden className="text-slate-300">·</span>
                  <span className="inline-flex items-center gap-0.5" data-testid={`docreq-reminders-${r.id}`}>
                    <Send size={9} /> {reminderCount} reminder{reminderCount === 1 ? "" : "s"}
                  </span>
                </>
              )}
              {r.email_sent_at && (
                <>
                  <span aria-hidden className="text-slate-300">·</span>
                  <span>Last activity {_fmtRel(r.email_sent_at)}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            {actions.includes("chase") && (
              <button type="button" onClick={() => onChase(r)} disabled={pendingAction === r.id}
                className="inline-flex items-center gap-1 h-6 px-2 rounded text-[10.5px] font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-40"
                data-testid={`docreq-action-chase-${r.id}`}>
                <Send size={10} /> Chase
              </button>
            )}
            {actions.includes("view") && (
              <button type="button" onClick={() => onView(r)}
                className="inline-flex items-center gap-1 h-6 px-2 rounded text-[10.5px] font-semibold text-slate-700 hover:bg-slate-50"
                data-testid={`docreq-action-view-${r.id}`}>
                <Eye size={10} /> View
              </button>
            )}
            {actions.includes("received") && (
              <button type="button" onClick={() => onReceived(r)} disabled={pendingAction === r.id}
                className="inline-flex items-center gap-1 h-6 px-2 rounded text-[10.5px] font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-40"
                data-testid={`docreq-action-received-${r.id}`}>
                <Check size={10} /> Mark Received
              </button>
            )}
            {actions.includes("verify") && (
              <button type="button" onClick={() => onVerify(r)} disabled={pendingAction === r.id}
                className="inline-flex items-center gap-1 h-6 px-2 rounded text-[10.5px] font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                data-testid={`docreq-action-verify-${r.id}`}>
                <ShieldCheck size={10} /> Verify
              </button>
            )}
            {actions.includes("cancel") && (
              <button type="button" onClick={() => onCancel(r)} disabled={pendingAction === r.id}
                className="inline-flex items-center gap-1 h-6 px-2 rounded text-[10.5px] font-semibold text-slate-500 hover:text-rose-700 hover:bg-rose-50 disabled:opacity-40"
                data-testid={`docreq-action-cancel-${r.id}`}>
                <XIcon size={10} />
              </button>
            )}
          </div>
        </button>

        {isExpanded && (
          <div className="border-t border-slate-100 px-3 py-2.5 bg-slate-50/30" data-testid={`docreq-activity-${r.id}`}>
            <p className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1.5">Activity</p>
            <ol className="relative pl-3 space-y-1">
              {_buildActivity(r).map((ev, i) => (
                <li key={i} className="text-[11.5px] text-slate-700 flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-300" />
                  <span className="font-medium">{ev.kind}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-500 tabular-nums">{_fmtRel(ev.at)}</span>
                  {ev.actor && <span className="text-slate-400">· {ev.actor}</span>}
                </li>
              ))}
              {_buildActivity(r).length === 0 && (
                <li className="text-[11.5px] text-slate-500">No activity recorded yet.</li>
              )}
            </ol>
            {r.note && (
              <p className="mt-2 text-[11.5px] text-slate-600 leading-relaxed whitespace-pre-line">{r.note}</p>
            )}
          </div>
        )}
      </li>
    );
  };

  const renderBucket = (key) => {
    const list = buckets[key];
    if (!list || list.length === 0) return null;
    const meta = _bucketMeta[key];
    const Icon = meta.icon;
    return (
      <div className="mt-3" data-testid={`docreq-bucket-${key}`}>
        <div className="flex items-center gap-1.5 mb-1.5">
          <Icon size={11} className={meta.tone} />
          <p className={`text-[10.5px] uppercase tracking-[0.12em] font-semibold ${meta.tone}`}>
            {meta.label} · {list.length}
          </p>
        </div>
        <ul className="space-y-1.5">{list.map(renderRow)}</ul>
      </div>
    );
  };

  return (
    <section
      className="rounded-xl bg-white border border-slate-100 p-3.5"
      data-testid="docreq-workspace"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-slate-500">Document Requests</p>
          <p className="mt-0.5 text-[11.5px] text-slate-500 tabular-nums" data-testid="docreq-summary">
            <span className="text-slate-700 font-semibold">{summary.outstanding}</span> Outstanding
            <span className="text-slate-300 mx-1.5">·</span>
            <span className={summary.overdue > 0 ? "text-rose-700 font-semibold" : "text-slate-700 font-semibold"}>{summary.overdue}</span> Overdue
            <span className="text-slate-300 mx-1.5">·</span>
            <span className="text-emerald-700 font-semibold">{summary.completed}</span> Completed
          </p>
        </div>
        <button
          type="button"
          onClick={onRequestDoc}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-semibold text-white transition-shadow hover:shadow-[0_8px_20px_-10px_rgba(124,58,237,0.55)]"
          style={{ background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)" }}
          data-testid="docreq-request-btn"
        >
          <Plus size={12} strokeWidth={2.4} /> Request
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="px-2 py-6 text-center" data-testid="docreq-empty">
          <p className="text-[13px] font-semibold text-slate-700">No document requests yet.</p>
        </div>
      ) : (
        <>
          {renderBucket("overdue")}
          {renderBucket("due_today")}
          {renderBucket("due_this_week")}
          {renderBucket("outstanding")}
          {buckets.completed.length > 0 && (
            <div className="mt-3" data-testid="docreq-bucket-completed-wrapper">
              <button
                type="button"
                onClick={() => setCompletedOpen((v) => !v)}
                className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.12em] font-semibold text-emerald-700 hover:text-emerald-800"
                data-testid="docreq-completed-toggle"
                aria-expanded={completedOpen}
              >
                {completedOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                Completed · {buckets.completed.length}
              </button>
              {completedOpen && (
                <ul className="mt-1.5 space-y-1.5" data-testid="docreq-bucket-completed">
                  {buckets.completed.map(renderRow)}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default DocumentRequestsWorkspace;
